import {
  ChangeEvent,
  forwardRef,
  Ref,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import Grid from "@mui/material/Grid";
import { FormCategory, Ratio } from "@simplyk/common";
import dynamic from "next/dynamic";
import { useSnackbar } from "notistack";
import { isMobileSafari } from "react-device-detect";
import { Control, FieldValues, Path, useWatch, UseFormRegister, UseFormSetValue } from "react-hook-form";

import { CroppedFile } from "../Crop/Crop";
// eslint-disable-next-line import/no-cycle
import { CropDialog } from "../Crop/CropDialog";

// Dynamically import SelectImageDialog to prevent SSR issues with Unsplash tRPC calls
const SelectImageDialog = dynamic(
  () => import("../SelectImageDialog/SelectImageDialog").then((mod) => mod.SelectImageDialog),
  {
    ssr: false,
  }
);

import { AmplitudeEvents } from "@/constants/amplitude";
import { LIMIT_10_MB } from "@/constants/image";
import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import { DashboardCard } from "@/dashboard-design-system/Card/DashboardCard";
import { zeffyCardClasses } from "@/dashboard-design-system/Card/types";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { isRequired } from "@/helpers/validators";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useTranslate } from "@/hooks/useTranslate";
import { useWidth } from "@/hooks/useWidth";
import { ImageDimensions } from "@/types/image";

type Sizes = [100, 100] | [188, 188] | [400, 400] | [800, 800] | [140, 140] | [1024, 576] | [2048, 1152];

export enum ImageType {
  Logo = "logo",
  Banner = "banner",
  Signature = "signature",
  OrganizationLogo = "organizationLogo",
  ItemVisual = "itemVisual",
  TicketBanner = "ticketBanner",
  ProfilePhoto = "profilePhoto",
}

export type RatioSizes =
  | {
      ratio: Ratio.Square;
      sizes: Sizes & ([100, 100] | [188, 188] | [400, 400] | [140, 140] | [800, 800]);
    }
  | {
      ratio: Ratio.Landscape;
      sizes: Sizes & ([1024, 576] | [2048, 1152]);
    };

export interface ImageUploaderHandle {
  setImage: (url: string | null) => void;
}

export interface ImageUploaderProps<T extends FieldValues> {
  className?: string;
  control: Control<T>;
  // Url of the image to display as a preview
  imageUrl?: string | null;
  disabled?: boolean;
  importLabel: string;
  isRequired?: boolean;
  name: Path<T>;
  onChange?: (imageDataUrl: string) => void;
  register: UseFormRegister<T>;
  required?: boolean;
  setDeleted?: () => void;
  setValue: UseFormSetValue<T>;
  "data-test"?: string;
  allowUnsplash?: boolean;
  maxDimensions: ImageDimensions | undefined;
  formType?: FormCategory;
  isEditing?: boolean;
  imageType: ImageType;
  handleFillWithPlaceholder?: () => Promise<void>;
  amplitudePayload?: Record<string, unknown>;
  disableCropOnMobile?: boolean;
  /** Whether to show the delete button when an image is present (default: true) */
  showDeleteButton?: boolean;
}

export const getSizeInBite = (dataUrl: string) => {
  const head = "data:image/png;base64,";
  return Math.round(((dataUrl.length - head.length) * 3) / 4);
};

const isHeicFormat = (file: File) => {
  return (
    file.type === "image/heic" ||
    file.type === "image/heif" ||
    file.name?.toLowerCase().endsWith(".heic") ||
    file.name?.toLowerCase().endsWith(".heif")
  );
};

/**
 * @deprecated Use ImageUploader instead
 * This component is used for old flow when we upload image at the same time we update the entity. Now we first upload and then update the entity.
 */
export const LegacyImageUploader = forwardRef(LegacyImageUploaderInner) as unknown as <T extends FieldValues>(
  props: ImageUploaderProps<T> & RatioSizes & RefAttributes<ImageUploaderHandle>
) => React.ReactElement<ImageUploaderProps<T> & RatioSizes>;

export function LegacyImageUploaderInner<T extends FieldValues>(
  {
    "data-test": dataTest,
    disabled = false,
    allowUnsplash,
    control,
    imageUrl,
    name,
    onChange: handleChange,
    register,
    required,
    setDeleted,
    setValue,
    isEditing,
    formType,
    imageType,
    amplitudePayload,
    disableCropOnMobile = false,
    showDeleteButton = true,
    className,
    importLabel,
    maxDimensions,
    handleFillWithPlaceholder,
    ratio,
  }: ImageUploaderProps<T> & RatioSizes,
  ref: Ref<ImageUploaderHandle>
): React.ReactElement<ImageUploaderProps<T> & RatioSizes> {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { isSmallScreen } = useMediaQuery();
  const { logAmplitudeEvent } = useAmplitude();

  const disableCrop = disableCropOnMobile && isSmallScreen;

  const [fileUrl, setFileUrl] = useState<string | undefined | null>(imageUrl ?? undefined);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [croppedFile, setCroppedFile] = useState<CroppedFile | undefined>(undefined);
  const [actualImageMetaData, setActualImageMetaData] = useState<
    { width: number; height: number; size: number; extension: string } | undefined
  >(undefined);

  const fillImageMetadata = useCallback((url: string) => {
    const isBlob = url.startsWith("data:");
    const extension = (isBlob ? url.split(";")[0].split("image/").pop() : url.split(".").pop()) ?? "none";
    const img = new Image();
    img.src = url;
    img.onload = function () {
      setActualImageMetaData({
        width: img.naturalWidth,
        height: img.naturalHeight,
        extension,
        size: getSizeInBite(url),
      });
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      setImage: (url: string | null) => {
        setFileUrl(url || undefined);
      },
    }),
    []
  );

  const inputRef = useRef<HTMLInputElement | null>(null);
  const value = useWatch({ control, name });

  const handleCloseCrop = useCallback(() => {
    setIsCropOpen(false);
  }, []);

  const updateFileUrl = useCallback(
    (image: string | undefined | null) => {
      setFileUrl(image);
      if (name && setValue) {
        setValue(name, image as never, { shouldDirty: true });
      }
      if (image) {
        handleChange?.(image);
        fillImageMetadata(image);
      }
    },
    [fillImageMetadata, handleChange, name, setValue]
  );

  // Resets fileUrl only when imageUrl is null, handling banner type switches in Live Form Editor without affecting uploads.
  useEffect(() => {
    if (imageUrl === null) {
      setFileUrl(undefined);
    }
  }, [imageUrl]);

  const handleValidateCrop = useCallback(
    (image: string) => {
      updateFileUrl(image);
      setIsCropOpen(false);
    },
    [updateFileUrl]
  );

  useEffect(() => {
    if (value === undefined) {
      register(name, isRequired(Boolean(required) && !fileUrl, "common.fileRequired"));
    }
  }, [name, fileUrl, required, value, register]);

  const onFileChange = useCallback(
    async (file: File) => {
      const imageDataUrl = URL.createObjectURL(file);
      if (isHeicFormat(file) && !isMobileSafari) {
        enqueueSnackbar(t("common", "heicConversionFailed"), { vibe: "danger" });
        logAmplitudeEvent(AmplitudeEvents.FormHEICImageUploadStarted, {
          file_type: file.type,
          ...amplitudePayload,
        });
      }
      setCroppedFile({ url: imageDataUrl, type: file.type });

      fillImageMetadata(imageDataUrl);
      setIsCropOpen(true);
    },
    [amplitudePayload, enqueueSnackbar, fillImageMetadata, logAmplitudeEvent, t]
  );

  const selectUnsplashImage = useCallback(
    (url: string) => {
      // TODO Add correct unsplash image type
      // Type is not normally sent with the unsplash image
      setCroppedFile({ url, type: "image/png" });
      fillImageMetadata(url);
      setIsCropOpen(true);
    },
    [fillImageMetadata]
  );

  const handlePickImage = useCallback(() => {
    logAmplitudeEvent(AmplitudeEvents.FormImageUploadStarted, {
      is_new_form: !isEditing,
      form_type: formType,
      original_image_type: imageType,
      ...amplitudePayload,
    });
    if (inputRef && inputRef.current) {
      inputRef.current.click();
    }
  }, [amplitudePayload, formType, imageType, isEditing, logAmplitudeEvent]);

  const onInputFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const files = event && event.target ? event.target.files : null;
      if (files && files[0]) {
        if (disableCrop && files[0].size <= LIMIT_10_MB) {
          logAmplitudeEvent(AmplitudeEvents.FormImageUploadStarted, {
            is_new_form: !isEditing,
            form_type: formType,
            original_image_type: imageType,
            ...amplitudePayload,
          });
          // Skip the crop modal for small screens due to upload issues
          const file = files[0];
          const reader = new FileReader();
          reader.onloadend = async () => {
            if (typeof reader.result === "string" && getSizeInBite(reader.result) <= LIMIT_10_MB) {
              updateFileUrl(reader.result);
              logAmplitudeEvent(AmplitudeEvents.FormImageUploadSubmitted, {
                is_new_form: !isEditing,
                form_type: formType,
                original_image_type: imageType,
                ...amplitudePayload,
              });
            }
          };
          reader.readAsDataURL(file);
        } else {
          setIsCropOpen(true);
          onFileChange(files[0]);
        }
      }
      event.target.value = ""; // reset the event allow to re-trigger the onChange, if we close the crop modal and re-choose the same file
    },
    [disableCrop, logAmplitudeEvent, isEditing, formType, imageType, amplitudePayload, updateFileUrl, onFileChange]
  );

  const onDelete = useCallback(() => {
    setDeleted?.();
    if (inputRef && inputRef.current) {
      inputRef.current.value = "";
    }
    updateFileUrl(null);
    logAmplitudeEvent(AmplitudeEvents.FormImageDeleted, {
      is_new_form: !isEditing,
      form_type: formType,
      // disable this props until fixed
      original_image_size: actualImageMetaData?.size,
      original_image_width: actualImageMetaData?.width,
      original_image_height: actualImageMetaData?.height,
      original_image_type: imageType,
      original_image_extension: actualImageMetaData?.extension,
      ...amplitudePayload,
    });
  }, [
    setDeleted,
    updateFileUrl,
    logAmplitudeEvent,
    isEditing,
    formType,
    actualImageMetaData?.size,
    actualImageMetaData?.width,
    actualImageMetaData?.height,
    actualImageMetaData?.extension,
    imageType,
    amplitudePayload,
  ]);

  const [selectImageModalOpen, setSelectImageModalOpen] = useState(false);
  const openSelectImageModal = useCallback(() => {
    logAmplitudeEvent(AmplitudeEvents.CustomizationClickBannerUnsplash, amplitudePayload);
    setSelectImageModalOpen(true);
  }, [amplitudePayload, logAmplitudeEvent]);

  const closeSelectImageModal = useCallback(() => {
    setSelectImageModalOpen(false);
  }, []);

  const currentDisabled = disabled;
  const ratioValue = ratio === Ratio.Square ? 1 : 16 / 9;

  const [imageUploaderWidth, setImageUploaderWidth] = useState(0);
  const imageUploaderHeight = imageUploaderWidth / ratioValue;
  const [imageUploaderRef, setImageUploaderRef] = useState<HTMLDivElement | null>(null);

  const handleSetImageUploaderRef = useCallback((ref: HTMLDivElement | null) => {
    setImageUploaderRef(ref);
  }, []);

  const { getWidths } = useWidth([imageUploaderRef]);
  useEffect(() => {
    const [columnWidth] = getWidths();
    setImageUploaderWidth(columnWidth);
  }, [getWidths]);

  return (
    <>
      {isCropOpen && (
        <CropDialog
          isOpen={isCropOpen}
          onCrop={handleValidateCrop}
          onClose={handleCloseCrop}
          croppedFile={croppedFile}
          ratio={ratioValue}
          maxDimensions={maxDimensions}
          imageType={imageType}
          actualImageMetaData={actualImageMetaData}
          fillImageMetadata={fillImageMetadata}
          onFileChange={onFileChange}
          handlePickImage={handlePickImage}
          setCroppedFile={setCroppedFile}
          amplitudePayload={{ ...amplitudePayload, is_new_form: !isEditing, form_type: formType }}
        />
      )}
      {selectImageModalOpen && (
        <SelectImageDialog
          isOpen={selectImageModalOpen}
          onClose={closeSelectImageModal}
          onSelectImage={selectUnsplashImage}
        />
      )}
      <DashboardCard
        variant="outlined"
        fullWidth
        noHover
        className={className}
        data-test={dataTest}
        ref={handleSetImageUploaderRef}
        style={{ height: imageUploaderHeight }}
        sx={(theme) => ({
          [`&.${zeffyCardClasses.root}`]: {
            position: "relative",
            height: "100%",
            backgroundColor: theme.palette.surface.neutral.supershy,
            borderColor: theme.palette.border.neutral.moderate,
            borderStyle: "dashed",
            overflow: "hidden",
            padding: 0,
            "&:hover": {
              backgroundColor: theme.palette.surface.neutral.supershy,
            },
          },
        })}
      >
        <input
          name={name}
          accept="image/*"
          style={{ display: "none" }}
          id={name}
          type="file"
          onChange={onInputFileChange}
          ref={inputRef}
          disabled={currentDisabled}
        />
        <Grid
          container
          sx={{
            cursor: currentDisabled ? "disabled" : "pointer",
            backgroundImage: `url(${fileUrl})`,
            backgroundSize: "cover",
            height: imageUploaderHeight,
          }}
          justifyContent="center"
          alignItems="center"
        >
          {!allowUnsplash && (
            <>
              {!fileUrl && (
                <Grid
                  container
                  justifyContent="center"
                  sx={{ p: 1, position: "relative" }}
                  spacing={1}
                  onClick={handlePickImage}
                >
                  <Grid item>
                    <DashboardButton variant="tonal" vibe="neutral" data-test={`${dataTest}-upload-button`}>
                      {importLabel}
                    </DashboardButton>
                  </Grid>
                </Grid>
              )}
            </>
          )}
          {allowUnsplash && (
            <Grid container justifyContent="center" sx={{ p: 1, position: "relative" }} spacing={1}>
              <Grid item>
                <Grid container spacing={2}>
                  {!fileUrl && (
                    <Grid item container alignItems="center" spacing={1} flexDirection="column" flexWrap="wrap">
                      <Grid item alignSelf="stretch">
                        <DashboardButton
                          variant="tonal"
                          vibe="neutral"
                          onClick={handlePickImage}
                          fullWidth
                          data-test={`${dataTest}-upload-button`}
                        >
                          {importLabel}
                        </DashboardButton>
                      </Grid>
                      {(process.env.NODE_ENV !== "production" || handleFillWithPlaceholder) && (
                        <>
                          <Grid item>
                            <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                              {t("common", "or")}
                            </DashboardTypography>
                          </Grid>
                          <Grid item alignSelf="stretch">
                            <DashboardButton
                              variant="outlined"
                              vibe="neutral"
                              onClick={handleFillWithPlaceholder ?? openSelectImageModal}
                              fullWidth
                            >
                              {handleFillWithPlaceholder
                                ? t("dashboard", "fillWithPlaceholderBanner")
                                : t("common", "selectImage")}
                            </DashboardButton>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
          {fileUrl && (
            <Grid item container height="100%" alignItems="center" justifyContent="center" spacing={2} padding={1}>
              <Grid item>
                <DashboardButton
                  variant="outlined"
                  vibe="neutral"
                  onClick={handlePickImage}
                  sx={{ backgroundColor: "white" }}
                  data-test={`${dataTest}-replace-button`}
                >
                  {t("common", "replaceFromFile")}
                </DashboardButton>
              </Grid>

              {showDeleteButton && (
                <Grid item>
                  <DashboardButton
                    variant="outlined"
                    vibe="neutral"
                    onClick={onDelete}
                    sx={{ backgroundColor: "white" }}
                    data-test={`${dataTest}-delete-button`}
                  >
                    {t("common", "deleteImage")}
                  </DashboardButton>
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      </DashboardCard>
    </>
  );
}
